import { useId, useState, type ReactNode } from 'react';
import { InfoCircle, Warning2 } from 'iconsax-reactjs';
import {
  formatPlainDate,
  formatPlainTime,
  instantToZonedParts,
  timeZoneLabel,
  useTimeZone,
  zonedPartsToInstant,
  type ZonedConversion,
} from '@/lib/time';
import { Field } from '../Form/Field';
import { RadioGroup } from '../Form/RadioGroup';
import { cx } from '../internal/cx';
import { DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';
import formStyles from '../Form/Form.module.css';
import styles from './DateTime.module.css';

export type Occurrence = 'earlier' | 'later';

export interface DateTimePickerProps {
  /** ISO-8601 UTC instant (e.g. "2026-03-08T07:30:00Z"), or "" when empty/incomplete. */
  value: string;
  /** Receives an ISO-8601 UTC instant, or "" when the date or time is cleared. */
  onChange: (instantIso: string) => void;
  /** IANA zone the user is entering wall-clock time in. Defaults to useTimeZone().timeZone. */
  timeZone?: string;
  /** Group label (rendered as the fieldset legend). */
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Earliest / latest selectable calendar date ("YYYY-MM-DD") in the zone. */
  minDate?: string;
  maxDate?: string;
  /** Minutes between suggested times. */
  step?: number;
  dateLabel?: string;
  timeLabel?: string;
  className?: string;
}

interface Parts {
  date: string;
  time: string;
  occurrence: Occurrence;
}

function partsFromInstant(value: string, timeZone: string): Parts {
  if (!value) return { date: '', time: '', occurrence: 'earlier' };
  try {
    const { date, time } = instantToZonedParts(value, timeZone);
    const earlier = zonedPartsToInstant(date, time, timeZone, 'earlier');
    const occurrence: Occurrence =
      earlier.status === 'ambiguous' &&
      new Date(earlier.instant).getTime() !== new Date(value).getTime()
        ? 'later'
        : 'earlier';
    return { date, time, occurrence };
  } catch {
    return { date: '', time: '', occurrence: 'earlier' };
  }
}

/**
 * Resolves the wall-clock date/time in `timeZone` to an instant. Times inside a spring-forward
 * gap are moved forward by the gap length ("compatible"), matching what clocks actually show.
 */
function resolve(
  date: string,
  time: string,
  timeZone: string,
  occurrence: Occurrence,
): ZonedConversion {
  const chosen = zonedPartsToInstant(date, time, timeZone, occurrence);
  if (chosen.status === 'nonexistent') {
    return zonedPartsToInstant(date, time, timeZone, 'compatible');
  }
  return chosen;
}

/**
 * Date + time entry for a real moment. The user picks a wall-clock time in `timeZone`; the value
 * is a UTC instant. DST gaps and overlaps are surfaced explicitly instead of silently guessed.
 */
export function DateTimePicker({
  value,
  onChange,
  timeZone: timeZoneProp,
  label,
  hint,
  error,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  step = 15,
  dateLabel = 'Date',
  timeLabel = 'Time',
  className,
}: DateTimePickerProps) {
  const context = useTimeZone();
  const timeZone = timeZoneProp ?? context.timeZone;
  const locale = context.locale;
  const baseId = useId();
  const hintId = hint ? `${baseId}-hint` : undefined;
  const errorId = error ? `${baseId}-error` : undefined;
  const describedBy = cx(hintId, errorId) || undefined;

  const [parts, setParts] = useState<Parts>(() => partsFromInstant(value, timeZone));
  // The value (and zone) our local parts correspond to. Differences mean an external change.
  const [synced, setSynced] = useState({ value, timeZone });
  if (synced.value !== value || synced.timeZone !== timeZone) {
    // External change (our own emissions update `synced` first, so an incomplete draft such as
    // "date chosen, time pending" survives its own "" emission).
    setSynced({ value, timeZone });
    setParts(partsFromInstant(value, timeZone));
  }

  function update(next: Parts) {
    setParts(next);
    if (!next.date || !next.time) {
      setSynced({ value: '', timeZone });
      if (value !== '') onChange('');
      return;
    }
    const conversion = resolve(next.date, next.time, timeZone, next.occurrence);
    setSynced({ value: conversion.instant, timeZone });
    if (conversion.instant !== value) onChange(conversion.instant);
  }

  const conversion =
    parts.date && parts.time ? resolve(parts.date, parts.time, timeZone, parts.occurrence) : null;
  const zoneInstant = conversion?.instant ?? (value || undefined);
  const zoneLabel = timeZoneLabel(timeZone, zoneInstant, locale);

  let dstNotice: ReactNode = null;
  if (conversion && parts.date && parts.time) {
    const wallTime = formatPlainTime(parts.time, locale);
    const dateText = formatPlainDate(parts.date, { locale, style: 'medium' });
    if (conversion.status === 'nonexistent') {
      const saved = instantToZonedParts(conversion.instant, timeZone);
      dstNotice = (
        <p className={cx(styles.dstNotice, styles.dstWarning)} role="status">
          <Warning2 size={16} color="currentColor" variant="Bold" aria-hidden="true" />
          <span>
            {wallTime} does not exist on {dateText} in {timeZone} (clocks spring forward); it will
            be saved as {formatPlainTime(saved.time, locale)}{' '}
            {timeZoneLabel(timeZone, conversion.instant, locale)}.
          </span>
        </p>
      );
    } else if (conversion.status === 'ambiguous') {
      const first = zonedPartsToInstant(parts.date, parts.time, timeZone, 'earlier');
      const second = zonedPartsToInstant(parts.date, parts.time, timeZone, 'later');
      dstNotice = (
        <div className={cx(styles.dstNotice, styles.dstInfo)}>
          <InfoCircle size={16} color="currentColor" variant="Bold" aria-hidden="true" />
          <RadioGroup
            className={styles.dstChoice}
            label={`${wallTime} occurs twice on ${dateText} in ${timeZone} (clocks fall back). Which one?`}
            orientation="horizontal"
            disabled={disabled}
            value={parts.occurrence}
            onChange={(occurrence) =>
              update({ ...parts, occurrence: occurrence === 'later' ? 'later' : 'earlier' })
            }
            options={[
              {
                value: 'earlier',
                label: `First (${wallTime} ${timeZoneLabel(timeZone, first.instant, locale)})`,
              },
              {
                value: 'later',
                label: `Second (${wallTime} ${timeZoneLabel(timeZone, second.instant, locale)})`,
              },
            ]}
          />
        </div>
      );
    }
  }

  return (
    <fieldset
      className={cx(formStyles.group, styles.dateTime, className)}
      aria-describedby={describedBy}
      disabled={disabled || undefined}
    >
      <legend className={cx(formStyles.label, formStyles.legend)}>
        {label}
        {required && (
          <span className={formStyles.required} aria-hidden="true">
            *
          </span>
        )}
      </legend>
      {hint && (
        <p id={hintId} className={formStyles.hint}>
          {hint}
        </p>
      )}
      <div className={styles.dateTimeRow}>
        <Field
          label={dateLabel}
          required={required}
          disabled={disabled}
          className={styles.dateField}
        >
          <DatePicker
            value={parts.date}
            min={minDate}
            max={maxDate}
            invalid={error ? true : undefined}
            aria-describedby={errorId}
            onChange={(date) => update({ ...parts, date })}
          />
        </Field>
        <Field
          label={timeLabel}
          required={required}
          disabled={disabled}
          className={styles.timeField}
        >
          <TimePicker
            value={parts.time}
            step={step}
            invalid={error ? true : undefined}
            aria-describedby={errorId}
            onChange={(time) => update({ ...parts, time, occurrence: 'earlier' })}
          />
        </Field>
        <span className={styles.zoneLabel} title={timeZone}>
          <span className="sr-only">Time zone: </span>
          {zoneLabel}
          <span className={styles.zoneName}> ({timeZone})</span>
        </span>
      </div>
      {dstNotice}
      {error && (
        <p id={errorId} className={formStyles.error}>
          <InfoCircle size={14} color="currentColor" variant="Bold" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </fieldset>
  );
}
